import { DASHBOARD_URL } from '../constants';
import { apiSlice, ORDER_LIST_TAG } from './apiSlice';

export const dashboardApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getStatisData: builder.query({
			query: ({ periodType, periodValue, orderPeriodType, orderPeriodValue } = {}) => {
				const params = {};
				if (periodType && periodValue) {
					params.periodType = periodType;
					params.periodValue = periodValue;
				}
				if (orderPeriodType && orderPeriodValue) {
					params.orderPeriodType = orderPeriodType;
					params.orderPeriodValue = orderPeriodValue;
				}
				return {
					url: `${DASHBOARD_URL}/statis/card`,
					params,
				};
			},
			providesTags: [ORDER_LIST_TAG],
			// 配置缓存策略：设置较短的缓存时间（25秒），略小于轮询间隔（30秒），确保数据实时性
			keepUnusedDataFor: 25,
		}),
		getOverdueStatData: builder.query({
			query: ({ summaryOnly } = {}) => ({
				url: `${DASHBOARD_URL}/statis/overdue`,
				params: summaryOnly ? { summaryOnly: '1' } : undefined,
			}),
			providesTags: ['OverdueStat'],
			keepUnusedDataFor: 60, // 缓存60秒，减少不必要的请求
		}),
		getOverdueStatBucket: builder.query({
			query: (bucket) => ({
				url: `${DASHBOARD_URL}/statis/overdue`,
				params: { bucket },
			}),
			keepUnusedDataFor: 60,
		}),
		getTotalDebtDetail: builder.query({
			query: ({ includeZero } = {}) => ({
				url: `${DASHBOARD_URL}/statis/total-debt-detail`,
				params: includeZero ? { includeZero: '1' } : {},
			}),
			keepUnusedDataFor: 30,
		}),
		getProcessingPendingOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/pending`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getApprovingOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/approving`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getDeliveringOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/delivering`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getAcceptingOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/accepting`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getPrePaymentOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/prepayment`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getFinalCostVerificationOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/final-cost-verification`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getContractOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/contract`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getAccountsReceivableOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/ar`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		getApplyingOrders: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/order/applying`,
			}),
			providesTags: [ORDER_LIST_TAG],
			keepUnusedDataFor: 5,
		}),
		previewStatementPdf: builder.mutation({
			query: (payload) => ({
				url: `${DASHBOARD_URL}/statement-pdf/preview`,
				method: 'POST',
				body: payload,
			}),
		}),
		createStatementPdfJob: builder.mutation({
			query: (payload) => ({
				url: `${DASHBOARD_URL}/statement-pdf/jobs`,
				method: 'POST',
				body: payload,
			}),
		}),
		getStatementPdfJobStatus: builder.query({
			query: (jobId) => ({
				url: `${DASHBOARD_URL}/statement-pdf/jobs/${jobId}`,
			}),
		}),
		downloadStatementPdfFile: builder.mutation({
			query: (jobId) => ({
				url: `${DASHBOARD_URL}/statement-pdf/jobs/${jobId}/file`,
				responseHandler: async (response) => {
					if (!response.ok) {
						const err = await response.json().catch(() => ({ message: '下载失败' }));
						throw Object.assign(new Error(err.message || '下载失败'), { status: response.status, data: err });
					}
					const blob = await response.blob();
					const disposition = response.headers.get('Content-Disposition') || '';
					const utf8Name = disposition.match(/filename\*=UTF-8''([^;]+)/i);
					const fileName = utf8Name?.[1] ? decodeURIComponent(utf8Name[1]) : 'statement.pdf';
					return { blob, fileName };
				},
				cache: 'no-cache',
			}),
		}),
		getStatementPdfConfig: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/statement-pdf/config`,
			}),
		}),
		getHomeDashboardLayout: builder.query({
			query: () => ({
				url: `${DASHBOARD_URL}/layout`,
			}),
			providesTags: ['HomeDashboardLayout'],
			keepUnusedDataFor: 300,
		}),
		putHomeDashboardLayout: builder.mutation({
			query: (layout) => ({
				url: `${DASHBOARD_URL}/layout`,
				method: 'PUT',
				body: { layout },
			}),
			async onQueryStarted(layout, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					dashboardApiSlice.util.updateQueryData('getHomeDashboardLayout', undefined, (draft) => {
						draft.layout = layout;
					}),
				);
				try {
					const { data } = await queryFulfilled;
					dispatch(
						dashboardApiSlice.util.updateQueryData('getHomeDashboardLayout', undefined, (draft) => {
							draft.layout = data?.layout ?? layout;
						}),
					);
				} catch {
					patchResult.undo();
				}
			},
		}),
		postResetHomeDashboardLayout: builder.mutation({
			query: () => ({
				url: `${DASHBOARD_URL}/layout/reset`,
				method: 'POST',
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;
					dispatch(
						dashboardApiSlice.util.updateQueryData('getHomeDashboardLayout', undefined, (draft) => {
							draft.layout = data?.layout;
						}),
					);
				} catch {
					// ignore
				}
			},
		}),
	}),
});

export const {
	useGetStatisDataQuery,
	useGetOverdueStatDataQuery,
	useLazyGetOverdueStatDataQuery,
	useLazyGetOverdueStatBucketQuery,
	useGetTotalDebtDetailQuery,
	useGetProcessingPendingOrdersQuery,
	useGetApprovingOrdersQuery,
	useGetDeliveringOrdersQuery,
	useGetAcceptingOrdersQuery,
	useGetPrePaymentOrdersQuery,
	useGetFinalCostVerificationOrdersQuery,
	useGetContractOrdersQuery,
	useGetAccountsReceivableOrdersQuery,
	useGetApplyingOrdersQuery,
	usePreviewStatementPdfMutation,
	useCreateStatementPdfJobMutation,
	useLazyGetStatementPdfJobStatusQuery,
	useDownloadStatementPdfFileMutation,
	useGetStatementPdfConfigQuery,
	useGetHomeDashboardLayoutQuery,
	usePutHomeDashboardLayoutMutation,
	usePostResetHomeDashboardLayoutMutation,
} = dashboardApiSlice;
